/**
 * @fileoverview Notices GNPC — messages d'état métier.
 *
 * Le portail citoyen doit éviter les messages techniques bruts. Ces notices
 * fournissent un ton actionnable, rassurant et cohérent pour les cas attendus :
 * succès, information, avertissement et erreur récupérable.
 */

import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { cn } from '../lib/utils.ts';

type NoticeVariant = 'info' | 'success' | 'warning' | 'danger';

export interface NoticeProps {
  /** Variante sémantique de la notice. */
  variant?: NoticeVariant;
  /** Titre court, orienté action ou état. */
  title: ReactNode;
  /** Corps du message. */
  children?: ReactNode;
  className?: string;
}

const VARIANTS: Record<NoticeVariant, string> = {
  info: 'border-quipux-blue-200 bg-quipux-blue-50 text-quipux-navy-950',
  success: 'border-status-paid/30 bg-status-paid/5 text-quipux-navy-950',
  warning: 'border-status-pending/35 bg-status-pending/10 text-quipux-navy-950',
  danger: 'border-status-failed/30 bg-status-failed/5 text-quipux-navy-950',
};

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
} satisfies Record<NoticeVariant, React.ComponentType<{ className?: string }>>;

/**
 * Couleur des icônes de variante.
 *
 * Contraste WCAG 2.1 AA : nuances 800 des statuts (voir `globals.css`),
 * pas la couleur de base — l'ambre #f59e0b sur fond `/10` ne fait que
 * 2,2:1, sous le minimum 3:1 exigé pour les éléments graphiques
 * (WCAG 1.4.11). Les nuances 800 donnent ≥ 6:1 sur tous les fonds soft.
 */
const ICON_COLORS: Record<NoticeVariant, string> = {
  info: 'text-quipux-blue-700',
  success: 'text-status-paid-800',
  warning: 'text-status-pending-800',
  danger: 'text-status-failed-800',
};

/**
 * Notice générique.
 *
 * @param props - Variante, titre, contenu et classe CSS.
 * @returns Bloc de message accessible et cohérent avec le ton GNPC.
 */
export function Notice({
  variant = 'info',
  title,
  children,
  className,
}: NoticeProps): React.JSX.Element {
  const Icon = ICONS[variant];

  return (
    <div className={cn('rounded-lg border p-4', VARIANTS[variant], className)}>
      <div className="flex gap-3">
        <Icon className={cn('mt-0.5 size-4 shrink-0', ICON_COLORS[variant])} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          {children && (
            <div className="text-muted-foreground mt-1 text-sm leading-6">{children}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Notice d'information neutre.
 *
 * @param props - Props de notice sans `variant`.
 * @returns Notice informative.
 */
export function InfoNotice(props: Omit<NoticeProps, 'variant'>): React.JSX.Element {
  return <Notice {...props} variant="info" />;
}

/**
 * Bannière de succès.
 *
 * @param props - Props de notice sans `variant`.
 * @returns Notice de succès.
 */
export function SuccessBanner(props: Omit<NoticeProps, 'variant'>): React.JSX.Element {
  return <Notice {...props} variant="success" />;
}

/**
 * Notice d'avertissement actionnable.
 *
 * @param props - Props de notice sans `variant`.
 * @returns Notice d'avertissement.
 */
export function WarningNotice(props: Omit<NoticeProps, 'variant'>): React.JSX.Element {
  return <Notice {...props} variant="warning" />;
}

/**
 * Notice d'erreur récupérable.
 *
 * @param props - Props de notice sans `variant`.
 * @returns Notice d'erreur avec ton actionnable.
 */
export function DangerNotice(props: Omit<NoticeProps, 'variant'>): React.JSX.Element {
  return <Notice {...props} variant="danger" />;
}
